
import { Component, Vue } from 'vue-property-decorator';
import {
  StockyMigrationModel,
  StockySupplierPreview,
  StockySupplierImportResult,
  StockyPOJobStatus,
  StockyVariantAssignmentPreview,
  StockyVariantAssignmentResult,
} from '@/models/stocky_migration';

const STATUS_COLORS: Record<string, string> = {
  draft: '#9E9E9E',
  confirmed: '#1976D2',
  partial_received: '#FF9800',
  received: '#4CAF50',
  completed: '#4CAF50',
  canceled: '#F44336',
};

@Component
export default class StockyDataMigration extends Vue {
  private step = 0;
  private loading = false;
  private initialLoading = true;
  private error: string | null = null;

  // Step 0: Connection
  private stockyApiKey = '';
  private isConnected = false;
  private hasSavedKey = false;
  private savedKeyMask = '';
  private isChangingKey = false;
  private previousStep = 0;

  // Step 1: Supplier preview
  private supplierPreview: StockySupplierPreview | null = null;
  private supplierConflictResolutions: Record<number, string> = {};
  private supplierImportResult: StockySupplierImportResult | null = null;
  private isSuppliersImported = false;

  // Step 2: PO status selection
  private selectedPOStatuses: string[] = [
    'draft-unarchived',
    'confirmed-unarchived',
    'draft-archived',
    'confirmed-archived',
  ];
  private poStatusOptions = [
    { value: 'confirmed-unarchived', label: 'Active', description: 'Confirmed, partially received, and received POs', icon: 'mdi-check-circle', color: 'success' },
    { value: 'draft-unarchived', label: 'Draft', description: 'POs still in draft status', icon: 'mdi-pencil-circle', color: 'grey' },
    { value: 'confirmed-archived', label: 'Archived (Confirmed)', description: 'Completed and archived confirmed POs', icon: 'mdi-archive', color: 'teal' },
    { value: 'draft-archived', label: 'Archived (Draft)', description: 'Archived draft POs', icon: 'mdi-archive-outline', color: 'blue-grey' },
  ];

  // Step 3: PO import
  private jobId: string | null = null;
  private jobStatus: StockyPOJobStatus | null = null;
  private pollingInterval: number | null = null;

  // Step 4: Variant assignments
  private variantCsvFile: File | null = null;
  private variantPreview: StockyVariantAssignmentPreview | null = null;
  private variantImportResult: StockyVariantAssignmentResult | null = null;
  private isDragover = false;
  private overrideExisting = false;
  private variantStepSkipped = false;

  private stepLabels = ['Connect', 'Suppliers', 'Select POs', 'Import', 'Products'];

  private migrationFeatures = [
    'Suppliers and contact information',
    'Tax types and rates',
    'All purchase orders (draft, confirmed, received, archived)',
    'PO line items matched to your Shopify products',
    'Receive history',
    'Supplier-product relationships',
  ];

  private conflictResolutionOptions = [
    { text: 'Skip', value: 'skip' },
    { text: 'Update', value: 'update' },
    { text: 'Keep Both', value: 'keep_both' },
  ];

  private supplierTableHeaders = [
    { text: 'Name', value: 'name' },
    { text: 'Email', value: 'contactEmail' },
    { text: 'Status', value: 'status', sortable: false },
    { text: 'Action', value: 'action', sortable: false },
  ];


  private variantSupplierHeaders = [
    { text: 'Supplier Name', value: 'name' },
    { text: 'Variants', value: 'variantCount' },
    { text: 'Status', value: 'matched', sortable: false },
  ];

  async mounted() {
    try {
      const status = await StockyMigrationModel.getConnection();
      if (status.hasKey) {
        this.hasSavedKey = true;
        this.savedKeyMask = status.maskedKey || '';
        this.isConnected = true;

        // If query param requests direct jump to products step
        if (this.$route.query.step === 'products') {
          this.step = 4;
          return;
        }

        // Skip Connect step — go directly to Suppliers
        this.step = 1;
        try {
          await this.fetchSupplierPreview();
        } catch {
          // Saved key is invalid — fall back to Connect step
          this.step = 0;
          this.isConnected = false;
          this.hasSavedKey = false;
          this.error = 'Your saved Stocky API key is no longer valid. Please enter a new key.';
        }
      }
    } catch {
      // Key missing or check failed — user will enter key manually
    } finally {
      this.initialLoading = false;
    }
  }

  get currentTitle(): string {
    switch (this.step) {
      case 0: return 'Migrate from Stocky';
      case 1: return 'Migrate from Stocky — Suppliers';
      case 2: return 'Migrate from Stocky — Select POs';
      case 3: return 'Migrate from Stocky — Import';
      case 4: return 'Migrate from Stocky — Product Assignments';
      default: return 'Migrate from Stocky';
    }
  }

  get isImportRunning(): boolean {
    return this.jobStatus !== null
      && (this.jobStatus.status === 'processing' || this.jobStatus.status === 'pending');
  }

  get isImportDone(): boolean {
    return this.jobStatus !== null
      && (this.jobStatus.status === 'completed' || this.jobStatus.status === 'error');
  }

  get showActionButton(): boolean {
    if (this.step === 3 && this.isImportRunning) return false;
    if (this.step === 3 && this.jobStatus?.status === 'error') return false;
    if (this.step === 4 && this.variantImportResult) return false;
    if (this.step === 4 && this.variantStepSkipped) return false;
    if (this.step === 4 && !this.variantCsvFile && !this.variantPreview) return false;
    return true;
  }

  get actionButtonText(): string {
    switch (this.step) {
      case 0:
        return this.isConnected ? 'Next' : 'Test Connection';
      case 1:
        return this.isSuppliersImported ? 'Next' : 'Import Suppliers';
      case 2:
        return 'Import Purchase Orders';
      case 3:
        return 'Next: Link Products';
      case 4:
        if (this.variantPreview) return 'Import Assignments';
        return 'Preview';
      default:
        return 'Next';
    }
  }

  get isActionDisabled(): boolean {
    if (this.loading) return true;
    if (this.step === 0 && !this.isConnected && !this.stockyApiKey.trim()) return true;
    if (this.step === 2 && this.selectedPOStatuses.length === 0) return true;
    if (this.step === 4 && !this.variantPreview && !this.variantCsvFile) return true;
    return false;
  }

  stepClass(idx: number): string {
    if (idx < this.step) return 'step-indicator--done';
    if (idx === this.step) return 'step-indicator--active';
    return 'step-indicator--pending';
  }

  goBack() {
    if (this.step > 0) {
      this.step -= 1;
    }
  }

  togglePOStatus(value: string) {
    const idx = this.selectedPOStatuses.indexOf(value);
    if (idx >= 0) {
      this.selectedPOStatuses.splice(idx, 1);
    } else {
      this.selectedPOStatuses.push(value);
    }
  }

  startKeyChange() {
    this.previousStep = this.step;
    this.isChangingKey = true;
    this.isConnected = false;
    this.step = 0;
    this.stockyApiKey = '';
    this.error = null;
  }

  cancelKeyChange() {
    this.isChangingKey = false;
    this.isConnected = true;
    this.step = this.previousStep;
    this.stockyApiKey = '';
    this.error = null;
  }

  resetConnection() {
    this.step = 0;
    this.isConnected = false;
    this.hasSavedKey = false;
    this.savedKeyMask = '';
    this.stockyApiKey = '';
    this.isChangingKey = false;
    this.previousStep = 0;
    this.error = null;
    this.supplierPreview = null;
    this.supplierImportResult = null;
    this.isSuppliersImported = false;
  }

  async handleAction() {
    this.error = null;

    if (this.step === 0) {
      if (this.isConnected) {
        this.loading = true;
        this.step = 1;
        await this.fetchSupplierPreview();
      } else {
        await this.testConnection();
      }
      return;
    }

    if (this.step === 1) {
      if (this.isSuppliersImported) {
        this.step = 2;
      } else {
        await this.importSuppliers();
      }
      return;
    }

    if (this.step === 2) {
      this.step = 3;
      await this.startPOImport();
      return;
    }

    if (this.step === 3) {
      // PO import done — move to variant assignments
      this.step = 4;
      return;
    }

    if (this.step === 4) {
      if (this.variantPreview) {
        await this.importVariantAssignments();
      } else {
        await this.previewVariantAssignments();
      }
    }
  }

  async testConnection() {
    this.loading = true;
    this.error = null;
    try {
      await StockyMigrationModel.connect(this.stockyApiKey);
      this.isConnected = true;
      this.hasSavedKey = true;
      this.savedKeyMask = this.stockyApiKey.slice(0, 6) + '...' + this.stockyApiKey.slice(-4);
    } catch (err: any) {
      this.error = err.response?.data?.error
        || err.message
        || 'Failed to connect to Stocky. Please check your API key.';
    } finally {
      this.loading = false;
    }
  }

  async fetchSupplierPreview() {
    this.loading = true;
    this.error = null;
    try {
      this.supplierPreview = await StockyMigrationModel.previewSuppliers();
      if (this.supplierPreview) {
        for (const supplier of this.supplierPreview.suppliers) {
          if (supplier.conflict) {
            this.$set(this.supplierConflictResolutions, supplier.stockyId, 'skip');
          }
        }
      }
    } catch (err: any) {
      this.error = err.response?.data?.error || err.message || 'Failed to load supplier preview.';
    } finally {
      this.loading = false;
    }
  }

  async importSuppliers() {
    this.loading = true;
    this.error = null;
    try {
      this.supplierImportResult = await StockyMigrationModel.importSuppliers(
        this.supplierConflictResolutions
      );
      this.isSuppliersImported = true;
    } catch (err: any) {
      this.error = err.response?.data?.error || err.message || 'Failed to import suppliers.';
    } finally {
      this.loading = false;
    }
  }

  async startPOImport() {
    this.error = null;
    try {
      const job = await StockyMigrationModel.importPurchaseOrders(this.selectedPOStatuses);
      this.jobId = job.jobId;
      this.jobStatus = { status: 'pending' };
      this.startPolling(job.jobId);
    } catch (err: any) {
      this.error = err.response?.data?.error || err.message || 'Failed to start purchase order import.';
    }
  }

  startPolling(jobId: string) {
    this.pollingInterval = window.setInterval(async () => {
      try {
        const status = await StockyMigrationModel.getImportStatus(jobId);
        this.jobStatus = status;

        if (status.status === 'completed' || status.status === 'error') {
          this.stopPolling();
        }
      } catch (err) {
        this.stopPolling();
        this.error = 'Failed to get import status.';
      }
    }, 2000);
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  getStatusColor(status: string): string {
    return STATUS_COLORS[status] || '#9E9E9E';
  }

  formatStatus(status: string): string {
    const labels: Record<string, string> = {
      draft: 'Draft',
      confirmed: 'Confirmed',
      partial_received: 'Partial',
      received: 'Received',
      completed: 'Completed',
      canceled: 'Canceled',
    };
    return labels[status] || status;
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value || 0);
  }

  handleFileDrop(event: DragEvent) {
    this.isDragover = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.name.toLowerCase().endsWith('.csv')) {
        this.variantCsvFile = file;
      } else {
        this.error = 'Only .csv files are allowed.';
      }
    }
  }

  handleFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (file.name.toLowerCase().endsWith('.csv')) {
        this.variantCsvFile = file;
      } else {
        this.error = 'Only .csv files are allowed.';
        input.value = '';
      }
    }
  }

  triggerFileInput() {
    (this.$refs.csvFileInput as HTMLInputElement)?.click();
  }

  clearCsvFile() {
    this.variantCsvFile = null;
    const input = this.$refs.csvFileInput as HTMLInputElement;
    if (input) input.value = '';
  }

  resetVariantStep() {
    this.variantPreview = null;
    this.variantCsvFile = null;
    this.overrideExisting = false;
    this.error = null;
    const input = this.$refs.csvFileInput as HTMLInputElement;
    if (input) input.value = '';
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  async previewVariantAssignments() {
    if (!this.variantCsvFile) return;
    this.loading = true;
    this.error = null;
    try {
      this.variantPreview = await StockyMigrationModel.previewVariantAssignments(this.variantCsvFile);
    } catch (err: any) {
      this.error = err.message || 'Failed to preview variant assignments.';
    } finally {
      this.loading = false;
    }
  }

  async importVariantAssignments() {
    if (!this.variantCsvFile) return;
    this.loading = true;
    this.error = null;
    try {
      this.variantImportResult = await StockyMigrationModel.importVariantAssignments(
        this.variantCsvFile,
        { overrideExisting: this.overrideExisting }
      );
    } catch (err: any) {
      this.error = err.message || 'Failed to import variant assignments.';
    } finally {
      this.loading = false;
    }
  }

  skipVariantStep() {
    this.variantStepSkipped = true;
  }

  navigateTo(path: string) {
    this.$router.push(path).catch(() => {});
  }

  beforeDestroy() {
    this.stopPolling();
  }
}
